import React, { useRef, useState } from 'react';

import { SplitButton } from 'primereact/splitbutton';
import { Dropdown } from 'primereact/dropdown';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';

import { ModelOfFleetInventory } from 'src/database';

import { useListCategoriesController } from 'src/controllers/Category/ListCategoriesController';
import { useListVehiclesController } from 'src/controllers/Vehicle/ListVehiclesController';
import LoadingScreen from 'src/components/LoadingScreen';
import { useFleetReportsController } from 'src/controllers/DataReports/Fleet/FleetReports';
import { useListEspecieController } from 'src/controllers/Especies/ListEspecieController';

const status = [
	{ label: 'Em manutenção', value: 0 },
	{ label: 'Pronto para uso', value: 1 },
];

const FleetIventoryReport: React.FC = () => {
	const { getEspecieListController } = useListEspecieController();
	const { getListController } = useListVehiclesController();
	const { getCategoryListController } = useListCategoriesController();
	const { getFleetReport, handleFleetReport } = useFleetReportsController();

	const [vehiclesFiltered, setVehiclesFiltered] = useState<ModelOfFleetInventory[]>([]);
	const [lisenseFilter, setLisenseFilter] = useState<number | null>(null);
	const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
	const [specieFilter, setSpecieFilter] = useState<string | null>(null);
	const [statusFilter, setStatusFilter] = useState<any | null>(null);

	const species = getEspecieListController.EspecieData.map((specie) => {
		return specie.DESCRICAO;
	});
	const categorys = getCategoryListController.categoriesData.map((category) => {
		return category.DESCRICAO;
	});
	const lisenses = getListController.vehiclesData.map((vehicle) => {
		return vehicle.ANOLICENCIADO;
	});

	const dataTable: any = useRef(null);

	const exportCSV = () => {
		dataTable.current.exportCSV();
	};

	const exportItems = [
		{
			label: 'Exportar PDF',
			icon: 'pi pi-upload',
			command: () => {
				handleFleetReport.generateFleetInventoryReport(vehiclesFiltered);
			},
		},
		{
			label: 'Exportar CSV',
			icon: 'pi pi-upload',
			command: exportCSV,
		},
	];

	const handleFilter = (vehicleData: ModelOfFleetInventory[]) => {
		let filterObj = {
			ANOLICENCIADO: lisenseFilter,
			DESCRICAO_CATEGORIA: categoryFilter || '',
			DESCRICAO_ESPECIE: specieFilter || '',
			STATUS: typeof statusFilter === 'number' ? statusFilter : '',
		};

		const filterKeys = Object.keys(filterObj);
		const filterValues = Object.values(filterObj);

		return vehicleData.filter((item: any) => {
			return filterKeys.every((value, eachKey) => {
				if (!filterValues[eachKey] && filterValues[eachKey] !== 0) {
					return true;
				}
				return filterValues[eachKey]?.toString().includes(item[value]);
			});
		});
	};

	if (getFleetReport.loading) {
		return <LoadingScreen />;
	}

	return (
		<div>
			<div style={{ height: '100%' }} className='card p-align-center p-justify-center'>
				<form className='p-grid p-fluid'>
					<div className='p-field p-col-12 p-md-2'>
						<label htmlFor='anoLic'>Ano Licenciamento</label>
						<Dropdown
							id='anoLic'
							value={lisenseFilter}
							showClear
							placeholder='Selecionar ano'
							onChange={(e) => {
								e.value ? setLisenseFilter(e.value) : setLisenseFilter(null);
							}}
							options={lisenses.filter((lisense, index) => {
								return lisenses.indexOf(lisense) === index;
							})}
						/>
					</div>

					<div className='p-field p-col-12 p-md-2'>
						<label htmlFor='categoria'>Categoria</label>
						<Dropdown
							id='categoria'
							value={categoryFilter}
							showClear
							placeholder='Selecionar categoria'
							onChange={(e) => {
								e.value ? setCategoryFilter(e.value) : setCategoryFilter('');
							}}
							options={categorys}
						/>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='especie'>Espécie</label>
						<Dropdown
							id='especie'
							value={specieFilter}
							showClear
							placeholder='Selecionar espécie'
							onChange={(e) => {
								e.value ? setSpecieFilter(e.value) : setSpecieFilter('');
							}}
							options={species}
						/>
					</div>

					<div className='p-field p-col-12 p-md-2'>
						<label htmlFor='status'>Status</label>
						<Dropdown
							id='status'
							value={statusFilter}
							showClear
							placeholder='Selecionar status'
							onChange={(e) => {
								typeof e.value === 'number' ? setStatusFilter(e.value) : setStatusFilter(null);
							}}
							options={status}
						/>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label>Para filtrar dados clique aqui</label>
						<SplitButton
							model={exportItems}
							icon='pi pi-search'
							label='Exibir relatório'
							onClick={() => {
								setVehiclesFiltered(handleFilter(getFleetReport.fleetInventoryData));
							}}
						/>
					</div>
				</form>
			</div>

			<ReportsComponent tableRef={dataTable} vehicleData={vehiclesFiltered} />
		</div>
	);
};

interface VehicleDataProps {
	vehicleData: ModelOfFleetInventory[];
	tableRef: any;
}

const ReportsComponent: React.FC<VehicleDataProps> = ({ vehicleData, tableRef }) => {
	const tableHeader = (
		<div className='table-header p-grid p-d-flex p-jc-between'>
			<h5 className='p-m-0'>Relatório de Inventário</h5>
		</div>
	);

	const HEADER_LICENCIAMENTO = (rowData: ModelOfFleetInventory) => {
		return (
			<React.Fragment>
				<span className='left-header'>LICENCIAMENTO</span>
				{rowData.ANOLICENCIADO}
			</React.Fragment>
		);
	};

	const HEADER_DESCRICAO = (rowData: ModelOfFleetInventory) => {
		return (
			<React.Fragment>
				<span className='left-header'>MODELO</span>
				{rowData.DESCRICAO}
			</React.Fragment>
		);
	};

	const HEADER_PLACA = (rowData: ModelOfFleetInventory) => {
		return (
			<React.Fragment>
				<span className='left-header'>PLACA</span>
				{rowData.PLACA}
			</React.Fragment>
		);
	};

	const HEADER_CAPACIDADECOMBUSTIVEL = (rowData: ModelOfFleetInventory) => {
		return (
			<React.Fragment>
				<span className='left-header'>CAPACIDADE (L)</span>
				{rowData.CAPACIDADECOMBUSTIVEL}
			</React.Fragment>
		);
	};

	const HEADER_ESPECIE = (rowData: ModelOfFleetInventory) => {
		return (
			<React.Fragment>
				<span className='left-header'>ESPÉCIE</span>
				{rowData.DESCRICAO_ESPECIE}
			</React.Fragment>
		);
	};

	const HEADER_STATUS = (rowData: ModelOfFleetInventory) => {
		return (
			<React.Fragment>
				<span className='left-header'>STATUS</span>
				{
					status.find((statusItem) => {
						return statusItem.value === rowData.STATUS;
					})?.label
				}
			</React.Fragment>
		);
	};

	const HEADER_CATEGORIA = (rowData: ModelOfFleetInventory) => {
		return (
			<React.Fragment>
				<span className='left-header'>CATEGORIA</span>
				{rowData.DESCRICAO_CATEGORIA}
			</React.Fragment>
		);
	};

	return (
		<div className='p-card p-p-4'>
			<DataTable
				ref={tableRef}
				header={tableHeader}
				className='responsive-table p-datatable-sm'
				dataKey='PLACA'
				autoLayout
				value={vehicleData}>
				<Column field='DESCRICAO' header='MODELO' body={HEADER_DESCRICAO}></Column>
				<Column field='PLACA' header='PLACA' body={HEADER_PLACA}></Column>
				<Column field='STATUS' header='STATUS' body={HEADER_STATUS}></Column>
				<Column
					field='CAPACIDADECOMBUSTIVEL'
					header='CAPACIDADE (L)'
					body={HEADER_CAPACIDADECOMBUSTIVEL}></Column>
				<Column field='ANOLICENCIADO' header='LICENCIAMENTO' body={HEADER_LICENCIAMENTO}></Column>
				<Column field='DESCRICAO_CATEGORIA' header='CATEGORIA' body={HEADER_CATEGORIA}></Column>
				<Column field='DESCRICAO_ESPECIE' header='ESPÉCIE' body={HEADER_ESPECIE}></Column>
			</DataTable>
		</div>
	);
};

export default FleetIventoryReport;
